import { useMemo } from 'react';
import {
  GrcNumberBox,
  GrcNumberBoxLabelMode,
  GrcNumberBoxStylingMode,
  GrcTextBox
} from '@grc/ui-package';
import { DEFAULT_SEED } from './tenant-types';
import { previewIdentity } from './identity-preview';
import { canonicalizeTemplate, parseTemplate } from './template-parser';

interface PatternEditorProps {
  idPrefix: string;
  seed: number;
  onSeedChange: (value: number) => void;
  template: string;
  onTemplateChange: (next: string) => void;
}

export default function PatternEditor({
  idPrefix,
  seed,
  onSeedChange,
  template,
  onTemplateChange
}: PatternEditorProps) {
  const setTemplate = (raw: string) => onTemplateChange(canonicalizeTemplate(raw));

  const { warnings } = useMemo(() => parseTemplate(template), [template]);
  const preview = useMemo(() => previewIdentity(template, seed), [template, seed]);

  return (
    <div className="pattern-editor">
      <div className="seed-template-inputs">
        <GrcNumberBox
          id={`${idPrefix}-seed`}
          label="Seed"
          labelMode={GrcNumberBoxLabelMode.Static}
          stylingMode={GrcNumberBoxStylingMode.Outlined}
          value={seed}
          onValueChanged={(e: { value?: number | null }) => onSeedChange(e.value ?? DEFAULT_SEED)}
          min={0}
        />
        <GrcTextBox
          id={`${idPrefix}-template`}
          label="Template"
          labelMode="static"
          stylingMode="outlined"
          value={template}
          onValueChanged={(e: { value?: string | null }) => setTemplate(e.value ?? '')}
          placeholder="e.g. S-{ID} or {PICKUPDATE:yyyyMMdd}-{ID}-{RANDOM[6]}"
          isValid={warnings.length === 0}
          validationError={{ message: warnings.map(w => w.message).join(' ') }}
          validationMessageMode="auto"
        />
      </div>

      <div className="identity-preview">
        <span className="preview-label">Preview:</span>
        <span className="preview-value">{preview || <em>(set a template)</em>}</span>
      </div>
    </div>
  );
}
